import type {
  Edge,
  Node,
} from '@vue-flow/core';
import type { UploadedAttachment } from '@/api/ai/aiApi';
import type { UserPublic } from '@/api/user/userType';
import type { CoachRequest } from '@/features/exercise-coach/protocol';
import { del, get, post } from '@/api/request.ts';
import { createRequestCoalescer } from '@/api/requestCoalescer.js';
import { getAuthToken } from '@/utils/auth';
import { connect } from '@/utils/sse';

const questionRequest = createRequestCoalescer({ ttlMs: 1500 });
const questionWrongAnswerStatsRequest = createRequestCoalescer({ ttlMs: 1500 });
const questionChatRequest = createRequestCoalescer({ ttlMs: 1500 });

function getQuestionRequestKey(questionId: string) {
  return `question:${getAuthToken()}:${questionId}`;
}

function clearQuestionRequestCache(questionId: string) {
  questionRequest.clear(getQuestionRequestKey(questionId));
}

function getQuestionWrongAnswerStatsRequestKey(questionId: string, teachingClassId?: string) {
  return `question-wrong-answer-stats:${getAuthToken()}:${questionId}:${teachingClassId ?? 'all'}`;
}

function clearQuestionWrongAnswerStatsRequestCache(questionId: string, teachingClassId?: string) {
  questionWrongAnswerStatsRequest.clear(getQuestionWrongAnswerStatsRequestKey(questionId, teachingClassId));
}

function getQuestionChatRequestKey(questionId: string) {
  return `question-chat:${getAuthToken()}:${questionId}`;
}

function clearQuestionChatRequestCache(questionId: string) {
  questionChatRequest.clear(getQuestionChatRequestKey(questionId));
}

interface Subject {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  official_review_status?: 'not_submitted' | 'pending' | 'approved' | 'rejected' | 'delisted';
  is_public_course?: boolean;
  teacher_name?: string;
}

interface Chapter {
  id: string;
  name: string;
  description: string;
  knowledge_points?: {
    id: string | number;
    name: string;
    description?: string;
  }[];
  children: {
    id: number;
    name: string;
    description: string;
    knowledge_points?: {
      id: string | number;
      name: string;
      description?: string;
    }[];
    children: Chapter[];
  }[];
  config: Record<string, any>;
}

type PreviewChapterNode = Chapter | Chapter['children'][number];

const LOCAL_TEACHER_PREVIEW_KEY = 'probfun_local_teacher_preview';
const LOCAL_STUDENT_PREVIEW_KEY = 'probfun_local_student_preview';

function isLocalTeacherPreview() {
  return import.meta.env.DEV && !localStorage.getItem('token') && sessionStorage.getItem(LOCAL_TEACHER_PREVIEW_KEY) === '1';
}

function isLocalStudentPreview() {
  return import.meta.env.DEV && !localStorage.getItem('token') && sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY) === '1';
}

function createPreviewChapter(
  id: number,
  name: string,
  description: string,
  sections: Array<{
    id: number;
    name: string;
    description?: string;
    knowledgePoints: Array<{ id: number; name: string }>;
  }>,
): Chapter {
  return {
    id: String(id),
    name,
    description,
    knowledge_points: [],
    children: sections.map(section => ({
      id: section.id,
      name: section.name,
      description: section.description ?? '',
      knowledge_points: section.knowledgePoints.map(point => ({
        id: point.id,
        name: point.name,
        description: '',
      })),
      children: [],
    })),
    config: {},
  };
}

const localPreviewChaptersBySubject: Record<string, Chapter[]> = {
  9001: [
    createPreviewChapter(9101, '随机事件与概率', '事件关系、概率定义与基本性质。', [
      { id: 9111, name: '随机事件', knowledgePoints: [{ id: 9201, name: '样本空间与随机事件' }, { id: 9202, name: '事件的关系与运算' }] },
      { id: 9112, name: '概率的定义', knowledgePoints: [{ id: 9203, name: '古典概型' }, { id: 9204, name: '概率的公理化定义' }] },
    ]),
    createPreviewChapter(9102, '条件概率与独立性', '条件概率、全概率公式与贝叶斯公式。', [
      { id: 9121, name: '条件概率', knowledgePoints: [{ id: 9211, name: '乘法公式' }, { id: 9212, name: '全概率公式' }] },
      { id: 9122, name: '事件独立性', knowledgePoints: [{ id: 9213, name: '贝叶斯公式' }, { id: 9214, name: '相互独立事件' }] },
    ]),
    createPreviewChapter(9103, '随机变量及其分布', '离散型与连续型随机变量。', [
      { id: 9131, name: '随机变量', knowledgePoints: [{ id: 9221, name: '分布函数' }, { id: 9222, name: '离散型随机变量' }] },
      { id: 9132, name: '常见分布', knowledgePoints: [{ id: 9223, name: '二项分布' }, { id: 9224, name: '正态分布' }] },
    ]),
  ],
  9002: [
    createPreviewChapter(9301, '第一章 线性方程组', '线性方程组的消元、求解与判别。', [
      { id: 9311, name: '1.1 高斯（Gauss）—若尔当（Jordan）算法', knowledgePoints: [{ id: 9401, name: '1.1.1 线性方程组的初等变换' }, { id: 9402, name: '1.1.2 矩阵的初等行变换' }, { id: 9403, name: '1.1.3 高斯—若尔当算法' }] },
      { id: 9312, name: '1.2 线性方程组的解的情况及其判别准则', knowledgePoints: [{ id: 9404, name: '1.2.1 阶梯形方程组解的情况及其判别准则' }, { id: 9405, name: '1.2.2 齐次线性方程组解的情况及其判别准则' }] },
      { id: 9313, name: '1.3 数域', knowledgePoints: [{ id: 9406, name: '数域的概念与基本性质' }] },
    ]),
    createPreviewChapter(9302, '第二章 矩阵', '矩阵运算、初等变换与可逆矩阵。', [
      { id: 9321, name: '2.1 矩阵的运算', knowledgePoints: [{ id: 9411, name: '矩阵的加法与数乘' }, { id: 9412, name: '矩阵乘法' }] },
      { id: 9322, name: '2.2 可逆矩阵', knowledgePoints: [{ id: 9413, name: '逆矩阵的定义' }, { id: 9414, name: '可逆矩阵的判定' }] },
    ]),
    createPreviewChapter(9303, '第三章 向量空间', '向量组、线性相关性与向量空间。', [
      { id: 9331, name: '3.1 向量组的线性相关性', knowledgePoints: [{ id: 9421, name: '线性组合' }, { id: 9422, name: '线性相关与线性无关' }] },
      { id: 9332, name: '3.2 向量空间', knowledgePoints: [{ id: 9423, name: '基与维数' }, { id: 9424, name: '坐标与基变换' }] },
    ]),
    createPreviewChapter(9304, '第四章 特征值与特征向量', '特征值、特征向量与矩阵对角化。', [
      { id: 9341, name: '4.1 特征值与特征向量', knowledgePoints: [{ id: 9431, name: '特征多项式' }, { id: 9432, name: '特征子空间' }] },
      { id: 9342, name: '4.2 矩阵对角化', knowledgePoints: [{ id: 9433, name: '相似矩阵' }, { id: 9434, name: '可对角化的判定条件' }] },
    ]),
  ],
};

function findLocalPreviewChapter(chapterId: string): PreviewChapterNode | null {
  const visit = (chapter: PreviewChapterNode): PreviewChapterNode | null => {
    if (String(chapter.id) === chapterId)
      return chapter;
    for (const child of chapter.children ?? []) {
      const matched = visit(child);
      if (matched)
        return matched;
    }
    return null;
  };

  for (const chapters of Object.values(localPreviewChaptersBySubject)) {
    for (const chapter of chapters) {
      const matched = visit(chapter);
      if (matched)
        return matched;
    }
  }
  return null;
}

function buildLocalPreviewDiagram(chapter: PreviewChapterNode) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const addChapterNode = (item: PreviewChapterNode, level: 1 | 2, parentId?: string) => {
    const nodeId = String(item.id);
    nodes.push({
      id: nodeId,
      position: { x: 0, y: 0 },
      data: { label: item.name, description: item.description, level, chapterId: nodeId },
    });
    if (parentId)
      edges.push({ id: `${parentId}-${nodeId}`, source: parentId, target: nodeId });

    for (const point of item.knowledge_points ?? []) {
      const pointId = `kp-${point.id}`;
      nodes.push({
        id: pointId,
        position: { x: 0, y: 0 },
        data: {
          label: point.name,
          description: point.description ?? '',
          level: level === 1 ? 2 : 3,
          chapterId: nodeId,
        },
      });
      edges.push({ id: `${nodeId}-${pointId}`, source: nodeId, target: pointId });
    }

    for (const child of item.children ?? [])
      addChapterNode(child, 2, nodeId);
  };

  addChapterNode(chapter, 1);
  return { nodes, edges };
}

interface KnowledgePoint {
  id: string;
  name: string;
  description: string;
  chapters: Chapter[];
  createdAt: string;
  updatedAt: string;
}

interface Choice {
  id: string;
  content: string;
  isCorrect: boolean;
  knowledgePoint: KnowledgePoint;
  createdAt: string;
  updatedAt: string;
  isChosen: boolean | null;
}

export interface Chat {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  status: string;
  hasReceivedText?: boolean;
  isUsingTool?: boolean;
  isThinking?: boolean;
  toolStatus?: Record<string, string>;
  coachResult?: string;
}

export interface Feedback {
  feedbackID: string;
  user: UserPublic;
  timestamp: string;
  content: string;
  category: string;
  question_id: string;
}

export interface AttemptState {
  status: 'idle' | 'in_progress' | 'submitted';
  attemptNo: number;
  startedAt: string | null;
  submittedAt: string | null;
}

interface Question {
  id: string;
  content: string;
  created_at: string;
  difficulty: string;
  fullAnswer: string;
  questionType: string;
  updatedAt: string;
  chapter?: {
    id: number | string;
    name?: string;
    description?: string;
  };
  choices: Choice[];
  answerRecords: Answer[];
  attemptState: AttemptState;
  isInWrongbook?: boolean;
  chats: Chat[] | null;
  conversationId?: string | null;
}

interface LocalPreviewQuestionTemplate {
  content: string;
  choices: string[];
  correctChoiceIndex: number;
  fullAnswer: string;
}

const localPreviewQuestionsByChapter: Record<string, LocalPreviewQuestionTemplate[]> = {
  9311: [
    {
      content: '对线性方程组的增广矩阵进行初等行变换时，下列哪项保持不变？',
      choices: ['方程组的解集', '矩阵中每个元素的值', '矩阵的行数和列数一定同时改变', '所有主元的位置'],
      correctChoiceIndex: 0,
      fullAnswer: '初等行变换对应方程的同解变形，因此不会改变原线性方程组的解集。',
    },
  ],
  9312: [
    {
      content: '齐次线性方程组 $Ax=0$ 一定具有哪一种解？',
      choices: ['零解', '唯一的非零解', '无穷多个非零解', '无解'],
      correctChoiceIndex: 0,
      fullAnswer: '令所有未知量都为零即可满足 $Ax=0$，所以齐次线性方程组一定有零解。',
    },
  ],
  9313: [
    {
      content: '下列哪个集合在通常的加法与乘法下构成数域？',
      choices: ['有理数集 $\\mathbb{Q}$', '正整数集', '自然数集', '奇数集'],
      correctChoiceIndex: 0,
      fullAnswer: '有理数集对加、减、乘、除（除数非零）封闭，因此构成数域。',
    },
  ],
  9321: [
    {
      content: '若矩阵 $A$ 为 $m\\times n$ 阶，矩阵 $B$ 为 $n\\times p$ 阶，则乘积 $AB$ 是几阶矩阵？',
      choices: ['$m\\times p$', '$n\\times n$', '$p\\times m$', '$m\\times n$'],
      correctChoiceIndex: 0,
      fullAnswer: '矩阵乘法要求内维相等，结果保留外维，因此 $AB$ 为 $m\\times p$ 阶矩阵。',
    },
  ],
  9322: [
    {
      content: '方阵 $A$ 可逆的充要条件是下列哪一项？',
      choices: ['$\\det(A)\\ne 0$', '$\\det(A)=0$', '$A$ 的所有元素都非零', '$A$ 是对称矩阵'],
      correctChoiceIndex: 0,
      fullAnswer: '方阵可逆当且仅当其行列式不为零。',
    },
  ],
  9331: [
    {
      content: '向量组 $\\alpha_1,\\alpha_2,\\ldots,\\alpha_s$ 线性相关意味着什么？',
      choices: ['存在不全为零的系数使其线性组合为零', '任意线性组合都不为零', '所有向量都必须是零向量', '向量个数必须等于维数'],
      correctChoiceIndex: 0,
      fullAnswer: '线性相关的定义正是存在一组不全为零的系数，使这些向量的线性组合等于零向量。',
    },
  ],
  9332: [
    {
      content: '一个 $n$ 维向量空间的任意一组基包含多少个向量？',
      choices: ['$n$ 个', '$n-1$ 个', '$n+1$ 个', '数量不确定'],
      correctChoiceIndex: 0,
      fullAnswer: '向量空间的维数定义为其任意一组基所含向量的个数，因此包含 $n$ 个向量。',
    },
  ],
  9341: [
    {
      content: '若非零向量 $x$ 满足 $Ax=\\lambda x$，则 $x$ 称为什么？',
      choices: ['$A$ 对应于 $\\lambda$ 的特征向量', '$A$ 的零向量', '$A$ 的行向量', '$A$ 的逆矩阵'],
      correctChoiceIndex: 0,
      fullAnswer: '根据定义，满足 $Ax=\\lambda x$ 的非零向量 $x$ 是矩阵 $A$ 对应于特征值 $\\lambda$ 的特征向量。',
    },
  ],
  9342: [
    {
      content: '$n$ 阶矩阵 $A$ 可对角化的一个充要条件是什么？',
      choices: ['$A$ 有 $n$ 个线性无关的特征向量', '$A$ 的所有元素都相同', '$A$ 的行列式等于零', '$A$ 必须是单位矩阵'],
      correctChoiceIndex: 0,
      fullAnswer: '矩阵可对角化当且仅当它具有 $n$ 个线性无关的特征向量。',
    },
  ],
};

function buildLocalPreviewQuestions(chapterId: string): Question[] | null {
  const templates = localPreviewQuestionsByChapter[chapterId];
  if (!templates)
    return null;

  return templates.map((template, questionIndex) => ({
    id: `local-preview-${chapterId}-${questionIndex + 1}`,
    content: template.content,
    created_at: '',
    difficulty: '基础',
    fullAnswer: template.fullAnswer,
    questionType: 'SC',
    updatedAt: '',
    chapter: { id: chapterId, name: findLocalPreviewChapter(chapterId)?.name ?? '' },
    choices: template.choices.map((content, choiceIndex) => ({
      id: `local-preview-${chapterId}-${questionIndex + 1}-${choiceIndex + 1}`,
      content,
      isCorrect: choiceIndex === template.correctChoiceIndex,
      knowledgePoint: {
        id: `local-preview-kp-${chapterId}`,
        name: findLocalPreviewChapter(chapterId)?.knowledge_points?.[0]?.name ?? '',
        description: '',
        chapters: [],
        createdAt: '',
        updatedAt: '',
      },
      createdAt: '',
      updatedAt: '',
      isChosen: null,
    })),
    answerRecords: [],
    attemptState: {
      status: 'idle',
      attemptNo: 0,
      startedAt: null,
      submittedAt: null,
    },
    chats: [],
    conversationId: null,
  }));
}

interface Answer {
  attachments?: UploadedAttachment[];
  id: string;
  isCorrect: boolean;
  answeredAt: string;
  selectedOptionIds?: string[];
  status?: string;
  subjectiveAnswer?: string;
}

interface AnalysisMetric {
  name: string;
  value: number;
  explanation: string;
}

interface AnalysisInsight {
  title: string;
  summary: string;
  evidence: string[];
  tags: string[];
}

interface ObjectiveAnalysis {
  metrics: AnalysisMetric[];
  insights: AnalysisInsight[];
  milestones: string[];
}

interface DialogueAnalysis {
  metrics: AnalysisMetric[];
  insights: AnalysisInsight[];
  representativeDialogues: string[];
}

interface LearningAnalysis {
  profileLabel: string;
  metrics: AnalysisMetric[];
  insights: AnalysisInsight[];
  samplePaths: string[][];
}

interface CognitiveAnalysis {
  thinkingStyles: string[];
  metrics: AnalysisMetric[];
  insights: AnalysisInsight[];
}

interface StudyPlan {
  weaknesses: string[];
  styleAdaptation: string;
  personalizedRecommendations: {
    title: string;
    description: string;
    actionType: string;
    targetKnowledge: string;
    triggerReason: string;
  }[];
  overallStrategy: string;
}

interface ChapterSummary {
  chapter: {
    id: string;
    name: string;
    description: string;
  };
  completedCount: number;
  correctCount: number;
  accuracy: number;
  questionCompletionRate: number;
}

export interface Analysis {
  objectiveAnalysis: ObjectiveAnalysis;
  dialogueAnalysis: DialogueAnalysis;
  learningAnalysis: LearningAnalysis;
  cognitiveAnalysis: CognitiveAnalysis;
  studyPlan: StudyPlan;
  chapterSummary: ChapterSummary[];
}

export interface WrongAnswerOptionStat {
  optionId: string;
  content: string;
  isCorrect: boolean;
  selectUserCount: number;
  proportion: number;
}

export interface WrongAnswerStats {
  questionId: string;
  questionContent: string;
  totalAnswerUsers: number;
  statistics: WrongAnswerOptionStat[];
}

export interface SubjectiveQuestionRecord {
  attachments: UploadedAttachment[];
  answeredAt: string;
  chapterId: number;
  chapterName: string;
  classId: string;
  className: string;
  id: number;
  isCorrect: boolean;
  questionId: string;
  realName: string;
  status?: string;
  subjectId: number;
  subjectiveAnswer: string;
  subjectName: string;
  userId: number;
  username: string;
}

async function fetchSubjectListApi() {
  if (isLocalTeacherPreview()) {
    return {
      subjects: [
        {
          id: '9001',
          name: '概率论教学示例',
          description: '用于预览概率论课程的章节与思维导图。',
          createdAt: '',
          updatedAt: '',
          is_public_course: false,
          official_review_status: 'not_submitted' as const,
        },
        {
          id: '9002',
          name: '线性代数发布示例',
          description: '用于预览线性代数课程的章节与思维导图。',
          createdAt: '',
          updatedAt: '',
          is_public_course: false,
          official_review_status: 'not_submitted' as const,
        },
      ],
    };
  }

  if (isLocalStudentPreview()) {
    return {
      subjects: [
        {
          id: '9002',
          name: '线性代数发布示例',
          description: '空间与向量的基础课程，用于检查公开课程卡片展示。',
          createdAt: '',
          updatedAt: '',
          is_public_course: true,
          official_review_status: 'approved' as const,
          teacher_name: '本地预览教师',
        },
      ],
    };
  }

  const result = await get<{
    subjects: Subject[];
  }>(`/assessment/subject/list/`);
  return result.data;
}

async function fetchQuestionApi(questionId: string, options?: { force?: boolean }) {
  if (options?.force) {
    clearQuestionRequestCache(questionId);
  }

  return questionRequest.run(getQuestionRequestKey(questionId), async () => {
    const result = await get<{
      question: Question;
    }>(`/assessment/question/${questionId}/`);
    return result.data;
  });
}

async function fetchWrongAnswerStatsApi(questionId: string, teachingClassId?: string, options?: { force?: boolean }) {
  if (options?.force) {
    clearQuestionWrongAnswerStatsRequestCache(questionId, teachingClassId);
  }

  return questionWrongAnswerStatsRequest.run(getQuestionWrongAnswerStatsRequestKey(questionId, teachingClassId), async () => {
    const result = await get<{
      question_id: string | number;
      question_content: string;
      total_answer_users: number;
      statistics: Array<{
        option_id: string | number;
        content: string;
        is_correct: boolean;
        select_user_count: number;
        proportion: number;
      }>;
    }>(`/assessment/question/${questionId}/wrong-answer-stats/`, teachingClassId ? { teaching_class_id: teachingClassId } : {});
    const data = result.data;
    return {
      questionId: String(data.question_id),
      questionContent: data.question_content,
      totalAnswerUsers: data.total_answer_users,
      statistics: data.statistics.map(stat => ({
        optionId: String(stat.option_id),
        content: stat.content,
        isCorrect: stat.is_correct,
        selectUserCount: stat.select_user_count,
        proportion: stat.proportion,
      })),
    };
  });
}

async function fetchQuestionListApi(chapterId: string) {
  if (import.meta.env.DEV && !localStorage.getItem('token')) {
    const questions = buildLocalPreviewQuestions(String(chapterId));
    if (questions)
      return { questions };
  }

  const result = await get<{
    questions: Question[];
  }>(`/assessment/question/list/`, { chapterId });
  return result.data;
}

async function fetchChapterListApi(subjectId: string) {
  if ((isLocalTeacherPreview() || isLocalStudentPreview()) && localPreviewChaptersBySubject[String(subjectId)])
    return { chapters: localPreviewChaptersBySubject[String(subjectId)] };

  const result = await get<{
    chapters: Chapter[];
  }>(`/assessment/chapter/list/`, { subjectId });
  return result.data;
}

async function fetchQuestionChatsApi(id: string, options?: { force?: boolean }) {
  if (options?.force) {
    clearQuestionChatRequestCache(id);
  }

  return questionChatRequest.run(getQuestionChatRequestKey(id), async () => {
    const result = await get<{
      conversation: {
        id: string;
        messages: Chat[];
        createdAt: string;
        updatedAt: string;
      };
    }>(`/assessment/question/chat/${id}/`);
    return result.data;
  });
}

async function answerQuestionApi(questionId: string, selectedOptionIds: string[], subjectiveAnswer: string, source?: 'wrongbook', attachmentIds?: number[]) {
  const result = await post<{
    question: Question;
    answerRecord: Answer;
  }>(`/assessment/question/answer/`, {
    questionId,
    selectedOptionIds,
    subjectiveAnswer,
    attachmentIds,
    source,
  });
  clearQuestionRequestCache(questionId);
  clearQuestionWrongAnswerStatsRequestCache(questionId);
  return result.data;
}

async function startQuestionAttemptApi(questionId: string, source?: 'wrongbook') {
  const result = await post<{
    question: Question;
  }>(`/assessment/question/attempt/start/`, {
    questionId,
    source,
  });
  clearQuestionRequestCache(questionId);
  return result.data;
}

async function draftQuestionApi(questionId: string, selectedOptionIds: string[]) {
  const result = await post<null>(`/assessment/question/draft/`, {
    questionId,
    selectedOptionIds,
  });
  return result.data;
}

async function clearQuestionChatApi(questionId: string) {
  const result = await del<null>(`/assessment/question/chat/${questionId}/`);
  clearQuestionChatRequestCache(questionId);
  return result.data;
}

async function chatWithAiAPi(
  questionId: string,
  content: string,
  thinkingEnabled: boolean,
  onopen: (response: Response) => Promise<void>,
  onmessage: (data: any) => void,
  onclose: () => void,
  onerror: (ev: Event) => void,
  controller: AbortController,
  coachRequest?: CoachRequest,
) {
  clearQuestionChatRequestCache(questionId);
  await connect({
    url: '/assessment/question/chat/',
    method: 'POST',
    body: {
      questionId,
      content,
      thinkingEnabled,
      ...coachRequest,
    },
    headers: {
      'Content-Type': 'application/json',
      'Accept': '*/*',
      'Authorization': getAuthToken(),
    },
    controller,
    onopen,
    onmessage: (ev) => {
      try {
        const data = JSON.parse(ev.data);
        onmessage(data);
      }
      catch (e) {
        console.error('Error parsing SSE message:', e);
      }
    },
    onclose,
    onerror,
    maxReconnects: coachRequest ? 0 : 1,
  });
}

async function fetchDiagramApi(chapterId: string) {
  if (isLocalTeacherPreview()) {
    const chapter = findLocalPreviewChapter(String(chapterId));
    if (chapter)
      return buildLocalPreviewDiagram(chapter);
  }

  const result = await get<{
    edges: Edge[];
    nodes: Node[];
  }>(`/assessment/chapter/knowledge-graph/`, { chapterId }, { timeout: 20000 });
  return result.data;
}

async function analysisApi(subjectId: string) {
  const result = await post<{
    analysis?: Analysis;
    status: string;
  }>(`/assessment/analysis/`, { subjectId });
  return result.data;
}

async function fetchSubjectiveQuestionApi(question_id: string, classId?: string) {
  const result = await get<{
    chapterId: number;
    questionId: string;
    questionType: string;
    records: SubjectiveQuestionRecord[];
    subjectId: number;
    total: number;
  }>(
    `/assessment/question/${question_id}/subjective-answer-records/`,
    classId ? { classId } : undefined,
  );
  return result.data;
}

async function fetchAnalysisStatusApi(subjectId: string) {
  const result = await get<{
    status: 'pending' | 'success' | 'fail' | 'not_found';
    hasReport: boolean;
    analysis?: Analysis;
    // 失败时也可能返回基础数据
    chapterSummary?: Array<{
      chapter: { id: string; name: string; description?: string };
      completedCount: number;
      correctCount?: number;
      accuracy: number;
      questionCompletionRate?: number;
    }>;
    stats?: {
      totalQuestions?: number;
      correctQuestions?: number;
      accuracy?: number;
      recentAccuracy?: number;
      weakChapterCount?: number;
      activeDays?: number;
      coverageRate?: number;
      coveredChapters?: number;
      totalChapters?: number;
    };
  }>(`/assessment/analysis/status/`, { subjectId });
  return result.data;
}

async function submitFeedbackApi(category: string = 'question', content: string, question_id: string, teaching_class_id: string | undefined) {
  const result = await post<{
    feedback: Feedback;
  }>(`/feedback/`, { content, category, question_id, teaching_class_id });
  return result.data;
}

/**
 * 获取实时学情分析数据
 * 注意：新字段可能不存在，前端需要防御性处理
 */
async function fetchRealTimeAnalysisApi(subjectId: string) {
  try {
    const result = await get<{
      status: string;
      hasReport?: boolean;
      stats?: {
        totalQuestions?: number;
        correctQuestions?: number;
        accuracy?: number;
        recentAccuracy?: number;
        weakChapterCount?: number;
        activeDays?: number;
        learningTrend?: {
          trend: 'improving' | 'declining' | 'stable';
          trendScore: number;
          dailyStats: Array<{
            date: string;
            questionsAttempted: number;
            questionsCorrect: number;
            accuracy: number;
          }>;
          summary: {
            avgDailyQuestions: number;
            avgAccuracy: number;
            bestDay: string | null;
            worstDay: string | null;
          };
        };
      };
      chapterSummary?: Array<{
        chapter: { id: string; name: string; description?: string };
        completedCount: number;
        correctCount?: number;
        accuracy: number;
        questionCompletionRate?: number;
        proficiency?: {
          level: 'master' | 'proficient' | 'intermediate' | 'beginner';
          score: number;
          stabilityScore?: number;
          masteryRate?: number;
          recommendation?: string;
        };
      }>;
    }>(`/assessment/analysis/`, { subjectId });
    return result.data;
  }
  catch (error) {
    console.warn('Real-time analysis API error:', error);
    return { status: 'error', stats: null, chapterSummary: null };
  }
}

/**
 * 获取班级统计数据
 * 注意：此API可能不存在，调用时需要 catch 处理
 */
async function fetchClassStatsApi(subjectId: string, teachingClassId?: string) {
  try {
    const result = await get<{
      hasClassData: boolean;
      classAvgAccuracy?: number;
      classAvgCoverageRate?: number;
      classAvgQuestionCount?: number;
      classAvgActiveDays?: number;
      userRank?: number;
      userRankPercentile?: number;
      rankDescription?: string;
      totalStudents?: number;
      chapterComparison?: Array<{
        chapterId: number;
        chapterName: string;
        myAccuracy: number;
        classAvgAccuracy: number;
        accuracyDelta: number;
        rank: number;
        percentile: number;
        advantage: 'ahead' | 'average' | 'behind';
        strengthLevel: 'high' | 'medium' | 'low';
      }>;
      chapterTrendScope?: Array<{
        chapterId: number;
        chapterName: string;
        chapterOrder?: number | null;
        sectionOrder?: number | null;
        classAccuracyMean: number;
        classAccuracyLower: number;
        classAccuracyUpper: number;
        classAccuracyLower2: number;
        classAccuracyUpper2: number;
        classCompletionMean: number;
        classCompletionLower: number;
        classCompletionUpper: number;
        classCompletionLower2: number;
        classCompletionUpper2: number;
      }>;
    }>(`/assessment/class-stats/`, {
      subjectId,
      ...(teachingClassId ? { teachingClassId } : {}),
    });
    return result.data;
  }
  catch (error) {
    console.warn('Class stats API not available:', error);
    return null; // 返回 null，前端判断后不显示班级对比
  }
}

export type {
  Chapter,
  Choice,
  CognitiveAnalysis,
  DialogueAnalysis,
  LearningAnalysis,
  ObjectiveAnalysis,
  Question,
  StudyPlan,
  Subject,
};

export {
  analysisApi,
  answerQuestionApi,
  chatWithAiAPi,
  clearQuestionChatApi,
  draftQuestionApi,
  fetchAnalysisStatusApi,
  fetchChapterListApi,
  fetchClassStatsApi,
  fetchDiagramApi,
  fetchQuestionApi,
  fetchQuestionChatsApi,
  fetchQuestionListApi,
  fetchRealTimeAnalysisApi,
  fetchSubjectiveQuestionApi,
  fetchSubjectListApi,
  fetchWrongAnswerStatsApi,
  startQuestionAttemptApi,
  submitFeedbackApi,
};
